import { Component, Input, OnInit, OnChanges, OnDestroy, DoCheck, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { distinctUntilChanged } from 'rxjs';

@Component({
  selector: 'app-uic-discount',
  templateUrl: './uic-discount.component.html',
  styleUrls: ['./uic-discount.component.css']
})
export class UicDiscountComponent implements OnInit, OnChanges, OnDestroy, DoCheck {
  @Input() FormGroup!: FormGroup;
  @Input() dependentOn!: string;
  discountType: string = '';
  discountValue: number = 0;
  totalAmount: number = 0;

  private priceSubscription: any;
  private discountRateSubscription: any;

  // Last price/discount values this component itself produced. Used by ngDoCheck to tell
  // an EXTERNAL silent patch (e.g. edit via patchValue with emitEvent:false into the same
  // FormGroup) apart from our own writes — the former must re-derive, the latter must not.
  private lastSeenPrice: any = undefined;
  private lastSeenDiscount: any = undefined;

  ngOnInit(): void {
    console.log(this.dependentOn);

    this.initializeComponent();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Detect changes to the FormGroup input
    if (changes['FormGroup'] && changes['FormGroup'].currentValue) {
      // Unsubscribe from previous subscriptions to avoid memory leaks
      if (this.priceSubscription) {
        this.priceSubscription.unsubscribe();
      }
      if (this.discountRateSubscription) {
        this.discountRateSubscription.unsubscribe();
      }
      // Reinitialize component when FormGroup changes
      this.initializeComponent();
    }
  }

  ngOnDestroy(): void {
    if (this.priceSubscription) {
      this.priceSubscription.unsubscribe();
    }
    if (this.discountRateSubscription) {
      this.discountRateSubscription.unsubscribe();
    }
  }

  /** Looks up a control name case-insensitively (UIC config may emit different casings). */
  private findControlKey(...candidates: string[]): string | undefined {
    const wanted = candidates.map(c => c.toLowerCase());
    return Object.keys(this.FormGroup.controls).find(
      key => wanted.includes(key.trim().toLowerCase())
    );
  }

  ngDoCheck(): void {
    if (!this.FormGroup) return;

    // Cast before comparing: the price input is a plain <input type="number"> with no
    // numeric value accessor, so a manually-typed value (e.g. Purchase Order's Price,
    // which has no item-price lookup) lands in the control as a STRING ("100"), while
    // lastSeenPrice is always stored as a NUMBER by rememberLastSeen(). Comparing them
    // uncast with !== is always true (string !== number), so the guard never holds and
    // reverseEngineerDiscount() re-fires on every change-detection tick, continuously
    // re-guessing the type from the raw amount and stomping the user's own selection.
    const price = Number(this.FormGroup.get(this.dependentOn)?.value);
    const discount = Number(this.FormGroup.get('discount')?.value);

    // Re-derive only when price/discount changed WITHOUT going through our own
    // calculateDiscount() (which keeps lastSeen* in sync). That happens when a parent
    // patches the form silently on edit (emitEvent:false, same FormGroup reference) —
    // valueChanges/ngOnChanges don't fire, so without this the previous row's discount
    // would stick. User typing routes through calculateDiscount() and is NOT re-derived.
    if (price !== this.lastSeenPrice || discount !== this.lastSeenDiscount) {
      this.reverseEngineerDiscount();
      // reverseEngineerDiscount() -> calculateDiscount() may re-patch discount; capture the
      // post-recompute values so our own write isn't picked up as another external change.
      this.lastSeenPrice = Number(this.FormGroup.get(this.dependentOn)?.value);
      this.lastSeenDiscount = Number(this.FormGroup.get('discount')?.value);
    }
  }

  /**
   * Re-derives discountType/discountValue and recalculates. Prefers the discountRate
   * control (source of truth, same as uic-tax's gstRateKey) when this masterType has one —
   * reverse-engineering from the `discount` AMOUNT is only a fallback for masterTypes with
   * no discountRate column, since the amount alone can't disambiguate % vs flat Net and can
   * be transiently stale (e.g. mid item-selection, before the new item's rate has landed).
   */
  private deriveDiscountState(): void {
    const discountRateKey = this.findControlKey('discountrate');
    if (discountRateKey) {
      this.discountType = 'Percentage';
      this.discountValue = Number(this.FormGroup.get(discountRateKey)?.value || 0);
      this.calculateDiscount();
    } else {
      this.reverseEngineerDiscount();
    }
  }

  private initializeComponent(): void {
    // Subscribe to price changes
    this.subscribeToPriceChanges();

    // Initialize discount values — prefer discountRate (direct %) over reverse-engineering
    // from the discount amount, the same way uic-tax prefers gstrate over the tax amount.
    const discountRateKey = this.findControlKey('discountrate');
    const discountRateValue = discountRateKey ? Number(this.FormGroup.get(discountRateKey)?.value || 0) : 0;

    if (discountRateValue > 0) {
      this.discountType = 'Percentage';
      this.discountValue = discountRateValue;
      this.calculateDiscount();
    } else if (this.FormGroup.get('discount')?.value != 0) {
      this.reverseEngineerDiscount();
    } else {
      this.discountType = '';
      this.discountValue = 0;
      this.calculateDiscount();
    }

    // Subscribe to discountRate changes — when patched externally (e.g. item selection),
    // sync the type/value and recalculate the discount amount. Handle 0 explicitly too
    // (an item with no discount must clear a previously-selected item's leftover %),
    // exactly like uic-tax's gstRateKey subscription handles a 0 rate.
    if (discountRateKey) {
      this.discountRateSubscription = this.FormGroup.get(discountRateKey)?.valueChanges
        .pipe(distinctUntilChanged())
        .subscribe((rate: any) => {
          const rateNum = Number(rate || 0);
          this.discountType = 'Percentage';
          this.discountValue = rateNum;
          this.calculateDiscount();
        });
    }
  }

  private subscribeToPriceChanges(): void {
    this.priceSubscription = this.FormGroup.get(this.dependentOn)?.valueChanges.pipe(distinctUntilChanged()).subscribe(() => {
      // Prefer the discountRate control (set moments before/after by the same item-select
      // patch) over reverse-engineering from the `discount` AMOUNT — the amount can be
      // transiently stale (still the previous item's, or not-yet-patched) at the exact
      // instant price changes, which is what caused the first-select-doesn't-patch bug.
      // Falls back to amount-based reverse-engineering for masterTypes with no discountRate
      // column, same as before (e.g. the "leftover % from a previously edited row" case).
      this.deriveDiscountState();
    });
  }

  reverseEngineerDiscount(): void {
    const discountAmount = this.FormGroup.get('discount')?.value;
    const price = this.FormGroup.get(this.dependentOn)?.value;

    if (!price || !discountAmount) {
      this.discountType = this.discountType === '' ? '' : this.discountType;
      this.discountValue = 0;
      this.calculateDiscount();
      return;
    }

    const amount = Number(discountAmount);
    const base = Number(price);

    // The stored value is only the discount AMOUNT — we can't know if the user typed a %
    // or a flat amount. Treat it as a percentage ONLY if a 2-decimal percentage reproduces
    // the exact amount; otherwise keep it as Net with the exact amount. This stops a flat
    // discount like 10 from being mis-read as 0.13% and silently drifting to 9.90 on edit.
    const roundedPct = +((amount / base) * 100).toFixed(2);
    const reconstructedAmount = this.roundToPaise((base * roundedPct) / 100);

    if (Math.abs(amount - reconstructedAmount) < 0.005) {
      this.discountType = 'Percentage';
      this.discountValue = roundedPct;
    } else {
      this.discountType = 'Net';
      this.discountValue = amount;
    }

    this.calculateDiscount();
  }

  calculateDiscount(): void {
    const price = Number(this.FormGroup?.get(this.dependentOn)?.value);
    const type = this.discountType;
    const inputValue = Number(this.discountValue);

    if (!price) {
      // Price isn't known yet — e.g. mid item-selection, where discountRate has already been
      // patched from the item but the jsexecutor that patches price is debounced 300ms behind
      // it. Zero the displayed discount amount (nothing to discount off a 0 base) but do NOT
      // wipe discountRate: doing so would permanently lose the item's % before the price-change
      // watcher gets a chance to recompute it once price actually lands (unlike uic-tax's
      // calculateTax(), which always re-affirms gstRate up front regardless of price).
      this.totalAmount = price;
      this.FormGroup?.get('discount')?.patchValue(0);
      this.rememberLastSeen(price, 0);
      return;
    }

    if (inputValue === 0) {
      this.totalAmount = price;
      this.FormGroup?.get('discount')?.patchValue(0);
      this.FormGroup?.get('discountRate')?.patchValue(0, { emitEvent: false });
      this.rememberLastSeen(price, 0);
      return;
    }

    let discountAmount = 0;

    if (type === 'Percentage') {
      discountAmount = (price * inputValue) / 100;
    } else if (type === 'Net') {
      discountAmount = inputValue;
    } else {
      // No type chosen yet. If a discount amount is already present (e.g. an edit just
      // patched it, and a price/rate change fired this before reverseEngineerDiscount ran),
      // derive the type from that amount instead of wiping it to 0. Guard against recursion:
      // only re-derive when the amount is non-zero (reverseEngineerDiscount sets a real type,
      // so the next calculateDiscount() takes the Net/Percentage branch, not this one).
      const existingDiscount = Number(this.FormGroup?.get('discount')?.value || 0);
      if (existingDiscount > 0) {
        this.reverseEngineerDiscount();
        return;
      }
      this.totalAmount = price;
      this.FormGroup?.get('discount')?.patchValue(0);
      this.FormGroup?.get('discountRate')?.patchValue(0, { emitEvent: false });
      this.rememberLastSeen(price, 0);
      return;
    }

    // The SP stores @Discount as DECIMAL(18,2), so round to paise here too — otherwise a
    // fractional percentage discount (e.g. 9.9047) shifts the taxable amount and the tax/
    // total drift one paisa from the saved record shown in the list below.
    discountAmount = this.roundToPaise(discountAmount);
    this.totalAmount = this.roundToPaise(price - discountAmount);

    // Patch the calculated discount amount to the form
    this.FormGroup?.get('discount')?.patchValue(discountAmount);

    // Patch discountRate (the percentage) — Net discount is back-calculated to a %
    const discountRate = type === 'Percentage'
      ? inputValue
      : this.roundToPaise((discountAmount / price) * 100);
    this.FormGroup?.get('discountRate')?.patchValue(discountRate, { emitEvent: false });

    this.rememberLastSeen(price, discountAmount);
  }

  /** Records the price/discount this component just wrote, so ngDoCheck ignores its own writes. */
  private rememberLastSeen(price: any, discount: any): void {
    this.lastSeenPrice = price;
    this.lastSeenDiscount = discount;
  }

  /** Rounds to 2 decimals (paise) to match the SP's DECIMAL(18,2) columns. */
  private roundToPaise(amountInRupees: number): number {
    return Math.round((amountInRupees + Number.EPSILON) * 100) / 100;
  }

  getDiscountTypeLabel(): string {
    const type = this.discountType;
    return type === 'Percentage' ? 'Disc %' : 'Disc Amt';
  }

  getFormControl(controlName: string): FormControl {
    return this.FormGroup.get(controlName) as FormControl;
  }

}